/**
 * Modal pour envoyer un message en diffusion à plusieurs destinataires.
 * Crée automatiquement des conversations individuelles.
 */
import React, { useState, useEffect } from 'react';
import { X, Send, Search, Users, CheckSquare, Square } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';

interface BroadcastRecipient {
  id: number;
  name: string;
  email: string;
  type: 'user' | 'employee' | 'client';
  role?: string; // 'admin', 'super_admin', 'apporteur' for users
  avatar?: string;
}

interface BroadcastMessageModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const BroadcastMessageModal: React.FC<BroadcastMessageModalProps> = ({ onClose, onSuccess }) => {
  const [recipients, setRecipients] = useState<BroadcastRecipient[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [selectedType, setSelectedType] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingRecipients, setLoadingRecipients] = useState(true);

  useEffect(() => {
    loadRecipients();
  }, []);

  const loadRecipients = async () => {
    try {
      setLoadingRecipients(true);
      const response = await api.get('/chat/broadcast/recipients');
      setRecipients(response.data);
    } catch (error: any) {
      console.error('Error loading recipients:', error);
      toast.error('Erreur lors du chargement des destinataires');
    } finally {
      setLoadingRecipients(false);
    }
  };

  const filteredRecipients = recipients.filter((recipient) => {
    // Filtrer par type
    if (selectedType !== 'all' && recipient.type !== selectedType) {
      return false;
    }

    // Filtrer par recherche
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      recipient.name.toLowerCase().includes(query) ||
      recipient.email.toLowerCase().includes(query)
    );
  });

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredRecipients.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredRecipients.map((r) => r.id)));
    }
  };

  const handleSend = async () => {
    if (!message.trim()) {
      toast.error('Veuillez saisir un message');
      return;
    }

    if (selectedIds.size === 0) {
      toast.error('Veuillez sélectionner au moins un destinataire');
      return;
    }

    try {
      setLoading(true);

      // Séparer les IDs par type
      const selectedRecipients = recipients.filter((r) => selectedIds.has(r.id));
      const recipient_user_ids = selectedRecipients
        .filter((r) => r.type === 'user')
        .map((r) => r.id);
      const recipient_employee_ids = selectedRecipients
        .filter((r) => r.type === 'employee')
        .map((r) => r.id);
      const recipient_buyer_ids = selectedRecipients
        .filter((r) => r.type === 'client')
        .map((r) => r.id);

      const response = await api.post('/chat/broadcast', {
        content: message,
        recipient_user_ids,
        recipient_employee_ids,
        recipient_buyer_ids,
      });

      toast.success(response.data.message || 'Messages envoyés avec succès');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error sending broadcast:', error);
      toast.error(
        error?.response?.data?.detail || 'Erreur lors de l\'envoi des messages'
      );
    } finally {
      setLoading(false);
    }
  };

  // Compter les types disponibles
  const typeCounts = {
    all: recipients.length,
    user: recipients.filter((r) => r.type === 'user').length,
    employee: recipients.filter((r) => r.type === 'employee').length,
    Client: recipients.filter((r) => r.type === 'client').length,
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Users className="h-6 w-6 text-purple-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                Diffusion de groupe
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-300"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
            Envoyez un message individualisé à plusieurs personnes en même temps
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
              Message *
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Saisissez votre message..."
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-slate-800 dark:text-slate-100"
            />
          </div>

          {/* Type filter */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedType('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${selectedType === 'all'
                  ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'
                }`}
            >
              Tous ({typeCounts.all})
            </button>
            {typeCounts.user > 0 && (
              <button
                onClick={() => setSelectedType('user')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${selectedType === 'user'
                    ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'
                  }`}
              >
                Utilisateurs ({typeCounts.user})
              </button>
            )}
            {typeCounts.employee > 0 && (
              <button
                onClick={() => setSelectedType('employee')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${selectedType === 'employee'
                    ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'
                  }`}
              >
                Employés ({typeCounts.employee})
              </button>
            )}
            {typeCounts.Client > 0 && (
              <button
                onClick={() => setSelectedType('client')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${selectedType === 'client'
                    ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'
                  }`}
              >
                Clients ({typeCounts.Client})
              </button>
            )}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un destinataire..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-slate-800 dark:text-slate-100"
            />
          </div>

          {/* Select all */}
          {filteredRecipients.length > 0 && (
            <div className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-slate-700">
              <button
                onClick={toggleSelectAll}
                className="flex items-center space-x-2 text-sm font-medium text-purple-600 hover:text-purple-700 dark:text-purple-400"
              >
                {selectedIds.size === filteredRecipients.length ? (
                  <CheckSquare className="h-5 w-5" />
                ) : (
                  <Square className="h-5 w-5" />
                )}
                <span>
                  {selectedIds.size === filteredRecipients.length
                    ? 'Tout désélectionner'
                    : 'Tout sélectionner'}
                </span>
              </button>
              <span className="text-sm text-gray-500 dark:text-slate-400">
                {selectedIds.size} sélectionné(s)
              </span>
            </div>
          )}

          {/* Recipients list */}
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {loadingRecipients ? (
              <div className="text-center py-8 text-gray-500 dark:text-slate-400">
                Chargement des destinataires...
              </div>
            ) : filteredRecipients.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-slate-400">
                {searchQuery
                  ? 'Aucun destinataire trouvé'
                  : 'Aucun destinataire disponible'}
              </div>
            ) : (
              filteredRecipients.map((recipient) => {
                const isSelected = selectedIds.has(recipient.id);

                return (
                  <button
                    key={`${recipient.type}-${recipient.id}`}
                    onClick={() => toggleSelect(recipient.id)}
                    className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors ${isSelected
                        ? 'bg-purple-50 border-2 border-purple-600 dark:bg-purple-900/20 dark:border-purple-500'
                        : 'hover:bg-gray-50 border-2 border-transparent dark:hover:bg-slate-800'
                      }`}
                  >
                    <div className="flex-shrink-0">
                      {isSelected ? (
                        <CheckSquare className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      ) : (
                        <Square className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                    <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                      {recipient.avatar ? (
                        <img
                          src={recipient.avatar}
                          alt={recipient.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <span className="text-sm font-medium text-gray-600 dark:text-slate-300">
                          {recipient.name.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-medium text-gray-900 dark:text-slate-100">
                        {recipient.name}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-slate-400">
                        {recipient.email}
                      </p>
                    </div>
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${recipient.type === 'user'
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                          : recipient.type === 'employee'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                            : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                        }`}
                    >
                      {recipient.type === 'user'
                        ? (recipient.role === 'super_admin' ? 'Super Admin' : recipient.role === 'admin' ? 'Admin' : recipient.role === 'apporteur' ? 'Apporteur' : 'Utilisateur')
                        : recipient.type === 'employee'
                          ? 'Employé'
                          : 'Client'}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between">
          <div className="text-sm text-gray-500 dark:text-slate-400">
            {selectedIds.size > 0 ? (
              <span className="font-medium text-purple-600 dark:text-purple-400">
                {selectedIds.size} destinataire(s) sélectionné(s)
              </span>
            ) : (
              <span>Sélectionnez au moins un destinataire</span>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleSend}
              disabled={loading || selectedIds.size === 0 || !message.trim()}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="h-4 w-4" />
              <span>
                {loading
                  ? 'Envoi...'
                  : `Envoyer à ${selectedIds.size} personne(s)`}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BroadcastMessageModal;
